/** Built-in target curves and example mechanisms for the gallery. */

import type { P2 } from "../synthesis/curve.js";
import { fourBar, sixBarStephenson } from "../kinematics/factories.js";
import type { MechanismSpec } from "../kinematics/types.js";

export interface TargetPreset {
  id: string;
  name: string;
  hint: string;
  make: () => P2[];
}

const ring = (n: number, f: (t: number) => P2): P2[] =>
  Array.from({ length: n }, (_, i) => f((2 * Math.PI * i) / n));

export const TARGET_PRESETS: TargetPreset[] = [
  {
    id: "line",
    name: "Straight stroke",
    hint: "The classic: turn rotation into dead-straight motion.",
    make: () => {
      const pts: P2[] = [];
      for (let i = 0; i <= 40; i++) pts.push([-1.6 + (3.2 * i) / 40, 0]);
      for (let i = 1; i < 8; i++) pts.push([1.6, -0.05 * i]);
      for (let i = 0; i <= 40; i++) pts.push([1.6 - (3.2 * i) / 40, -0.4]);
      for (let i = 1; i < 8; i++) pts.push([-1.6, -0.4 + 0.05 * i]);
      return pts;
    },
  },
  {
    id: "ellipse",
    name: "Ellipse",
    hint: "A smooth closed oval.",
    make: () => ring(120, (t) => [1.7 * Math.cos(t), Math.sin(t)]),
  },
  {
    id: "egg",
    name: "Egg",
    hint: "Asymmetric oval — harder than it looks.",
    make: () =>
      ring(120, (t) => {
        const r = 1 + 0.28 * Math.cos(t);
        return [1.4 * r * Math.cos(t), 1.1 * r * Math.sin(t)];
      }),
  },
  {
    id: "figure8",
    name: "Figure eight",
    hint: "Lemniscate — needs a self-crossing coupler curve.",
    make: () =>
      ring(160, (t) => [1.8 * Math.sin(t), 1.1 * Math.sin(t) * Math.cos(t) * 2]),
  },
  {
    id: "dcurve",
    name: "D-curve",
    hint: "Flat on one side, bulged on the other.",
    make: () => {
      const pts: P2[] = [];
      for (let i = 0; i <= 60; i++) {
        const t = -Math.PI / 2 + (Math.PI * i) / 60;
        pts.push([1.2 * Math.cos(t), 1.4 * Math.sin(t)]);
      }
      for (let i = 1; i <= 30; i++) pts.push([0, 1.4 - (2.8 * i) / 30]);
      return pts;
    },
  },
  {
    id: "heart",
    name: "Heart",
    hint: "A cusp and two lobes.",
    make: () =>
      ring(160, (t) => {
        const x = 16 * Math.sin(t) ** 3;
        const y =
          13 * Math.cos(t) -
          5 * Math.cos(2 * t) -
          2 * Math.cos(3 * t) -
          Math.cos(4 * t);
        return [x / 9, y / 9];
      }),
  },
  {
    id: "bean",
    name: "Kidney",
    hint: "A gentle concave pocket.",
    make: () =>
      ring(140, (t) => {
        const r = 1 + 0.45 * Math.cos(t) - 0.25 * Math.sin(2 * t);
        return [1.5 * r * Math.cos(t), 1.2 * r * Math.sin(t)];
      }),
  },
  {
    id: "triangle",
    name: "Rounded triangle",
    hint: "Three near-straight edges joined by fillets.",
    make: () =>
      ring(150, (t) => {
        const k = 0.35;
        const x = Math.cos(t) + k * Math.cos(2 * t);
        const y = Math.sin(t) - k * Math.sin(2 * t);
        return [1.6 * x, 1.6 * y];
      }),
  },
];

export interface MechanismPreset {
  id: string;
  name: string;
  hint: string;
  topology: "fourbar" | "stephenson3";
  make: () => MechanismSpec;
}

export const MECHANISM_PRESETS: MechanismPreset[] = [
  {
    id: "hoeken",
    name: "Hoeken straight-line",
    hint: "Grashof crank-rocker with a long straight coupler stroke.",
    topology: "fourbar",
    make: () =>
      fourBar({
        ground2: [0, 0],
        ground4: [2, 0],
        crank: 1,
        coupler: 2.5,
        rocker: 2.5,
        couplerPoint: [5, 0],
      }),
  },
  {
    id: "chebyshev",
    name: "Chebyshev lambda",
    hint: "Another approximate straight-line four-bar, more compact.",
    topology: "fourbar",
    make: () =>
      fourBar({
        ground2: [-2, 0],
        ground4: [2, 0],
        crank: 2.5,
        coupler: 2.5,
        rocker: 2.5,
        couplerPoint: [2.5, 0],
        phase: Math.PI / 2,
      }),
  },
  {
    id: "dwell",
    name: "Bean coupler",
    hint: "A four-bar whose tracer draws a rounded triangle with a near-dwell.",
    topology: "fourbar",
    make: () =>
      fourBar({
        ground2: [-0.9, 0],
        ground4: [0.9, -0.2],
        crank: 0.42,
        coupler: 1.5,
        rocker: 1.35,
        couplerPoint: [0.7, 0.85],
      }),
  },
  {
    id: "stephenson",
    name: "Stephenson-III six-bar",
    hint: "Extra dyad → richer, curlier coupler curves than any four-bar.",
    topology: "stephenson3",
    make: () =>
      sixBarStephenson({
        ground2: [-0.7, 0],
        ground4: [0.7, 0],
        ground6: [0.1, 1.15],
        crank: 0.26,
        couplerAB: 1.05,
        rocker: 0.95,
        couplerDA: 0.62,
        couplerDB: 0.68,
        link5: 0.8,
        link6: 0.72,
        couplerPoint: [0.5, 0.25],
      }),
  },
];
